import React, { ReactElement, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import DecimalInput from '../../../../../../../1stlog-ui/src/components/DecimalInput'
import Field, {
  Label,
} from '../../../../../../../1stlog-ui/src/components/Field'
import { formatDecimal } from '../../../../../../../1stlog-ui/src/components/Field/format'
import useFieldValue from '../../../../../../../1stlog-ui/src/components/Field/useFieldValue'
import {
  composeValidators,
  maxValue,
  mustBeFloat,
  mustBePositive,
  required,
} from '../../../../../../../1stlog-ui/src/components/Field/Validation'
import InlineEditorInvalid from '../../../../../../../1stlog-ui/src/components/InlineEditor/InlineEditorInvalid'
import { useProductSettings } from '../../../../Products'

const XWrapper = styled.i`
  font-size: 1.8em;
  margin: 0 5px;
  _margin-top: 10px;
`

const X = (): ReactElement => (
  <XWrapper
    className="fal fa-times align-middle mx-2 mt-2"
    style={{ fontSize: '1.1rem' }}
  />
)

const FieldBox = styled.div`
  max-width: 100px;
`

type TMeasurementsProps = {
  disabled?: boolean
  readonly?: boolean
}

const Measurements = (props: TMeasurementsProps): ReactElement => {
  const { disabled, readonly } = props

  const {
    maxLength = 0,
    maxHeight = 0,
    maxWidth = 0,
    isMeasurementsHidden,
    measurementsMandantory,
  } = useProductSettings()

  const isMeasurementsOptional = useMemo(() => {
    return !measurementsMandantory
  }, [measurementsMandantory])

  const width = useFieldValue<number>('width')
  const height = useFieldValue<number>('height')
  const length = useFieldValue<number>('length')

  const getValidateLength = useCallback(
    () =>
      composeValidators(
        mustBeFloat,
        isMeasurementsOptional ? undefined : required,
        maxLength > 0 ? maxValue(maxLength) : undefined,
        !isMeasurementsOptional ? mustBePositive : undefined,
      ),
    [isMeasurementsOptional, maxLength],
  )

  const getValidateWidth = useCallback(
    () =>
      composeValidators(
        mustBeFloat,
        isMeasurementsOptional ? undefined : required,
        maxWidth > 0 ? maxValue(maxWidth) : undefined,
        !isMeasurementsOptional ? mustBePositive : undefined,
      ),
    [isMeasurementsOptional, maxWidth],
  )

  const getValidateHeight = useCallback(
    () =>
      composeValidators(
        mustBeFloat,
        isMeasurementsOptional ? undefined : required,
        maxHeight > 0 ? maxValue(maxHeight) : undefined,
        !isMeasurementsOptional ? mustBePositive : undefined,
      ),
    [isMeasurementsOptional, maxHeight],
  )

  if (isMeasurementsHidden) {
    return <></>
  }

  return (
    <>
      {readonly && (
        <div className="text-center">
          <Label>
            {i18n.t('measurements', 'Abmessungen')} (
            {i18n.t('length_short', 'L')} x {i18n.t('width_short', 'B')} x{' '}
            {i18n.t('height_short', 'H')} {i18n.t('in_cm', 'in cm')})
          </Label>

          <h5 className="mt-1">
            {isNaN(length) || isNaN(width) || isNaN(height) ? (
              <InlineEditorInvalid />
            ) : (
              <span>
                {formatDecimal(String(length), 0)} x{' '}
                {formatDecimal(String(width), 0)} x{' '}
                {formatDecimal(String(height), 0)} cm
              </span>
            )}
          </h5>
        </div>
      )}
      <div className="form-group mb-1" hidden={readonly}>
        <Label required={!isMeasurementsOptional}>
          {`${i18n.t('measurements', 'Abmessungen')} (${i18n.t(
            'length_short',
            'L',
          )} x
${i18n.t('width_short', 'B')} x ${i18n.t('height_short', 'H')}
${i18n.t('in_cm', 'in cm')})`}
        </Label>
        <div>
          <div className="d-flex align-items-start">
            <FieldBox>
              <Field
                autofocus
                name="length"
                key={`${isMeasurementsOptional},${maxLength}`}
                validate={getValidateLength()}
                required={!isMeasurementsOptional}
              >
                <DecimalInput
                  placeholder={i18n.t('length_short', 'L')}
                  disabled={disabled}
                  minimumFractionDigits={2}
                />
              </Field>
            </FieldBox>
            <X />
            <FieldBox>
              <Field
                autofocus
                name="width"
                key={`${isMeasurementsOptional},${maxWidth}`}
                validate={getValidateWidth()}
                required={!isMeasurementsOptional}
              >
                <DecimalInput
                  placeholder={i18n.t('width_short', 'B')}
                  disabled={disabled}
                  minimumFractionDigits={2}
                />
              </Field>
            </FieldBox>
            <X />
            <FieldBox>
              <Field
                autofocus
                name="height"
                key={`${isMeasurementsOptional},${maxHeight}`}
                validate={getValidateHeight()}
                required={!isMeasurementsOptional}
              >
                <DecimalInput
                  placeholder={i18n.t('height_short', 'H')}
                  disabled={disabled}
                  minimumFractionDigits={2}
                />
              </Field>
            </FieldBox>
          </div>
        </div>
      </div>
    </>
  )
}

export default Measurements
